import { useEffect, useState } from 'react'
import { CheckCircle2, AlertTriangle, XCircle, Circle, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface Component {
  name: string
  kind: string
  status: string
  checked_at: string | null
}

interface StatusData {
  overall: string
  components: Component[]
  summary: { operational: number; degraded: number; outage: number; unknown: number }
  generated_at: string
}

const STATUS_VISUAL: Record<string, { icon: any; tone: string; label: string }> = {
  operational: { icon: CheckCircle2, tone: 'text-emerald',     label: 'Operational' },
  degraded:    { icon: AlertTriangle, tone: 'text-amber-light', label: 'Degraded' },
  outage:      { icon: XCircle,      tone: 'text-rose',         label: 'Outage' },
  unknown:     { icon: Circle,       tone: 'text-text-muted',   label: 'Unknown' },
}

export default function StatusPagePublic() {
  const [data, setData] = useState<StatusData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const pull = () => {
      api.get('/api/status-page')
        .then(r => setData(r.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false))
    }
    pull()
    const t = setInterval(pull, 60_000)
    return () => clearInterval(t)
  }, [])

  if (loading) return <div className="card p-6 text-center text-text-muted text-sm">Loading status…</div>
  if (!data) return <div className="card p-6 text-center text-text-muted text-sm">Status unavailable.</div>

  const overall = STATUS_VISUAL[data.overall] || STATUS_VISUAL.unknown
  const Icon = overall.icon

  // Group components by kind
  const grouped: Record<string, Component[]> = {}
  for (const c of data.components) {
    grouped[c.kind] = grouped[c.kind] || []
    grouped[c.kind].push(c)
  }

  return (
    <div className="space-y-4 animate-fade-in max-w-4xl mx-auto">
      <div className={clsx('rounded-xl p-6 ring-2',
        data.overall === 'operational' ? 'bg-emerald/10 ring-emerald/40'
        : data.overall === 'degraded'  ? 'bg-amber/10 ring-amber/40'
        : data.overall === 'outage'    ? 'bg-rose/10 ring-rose/40'
        : 'bg-surface-card ring-border')}>
        <div className="flex items-center gap-3">
          <Icon size={36} className={overall.tone} />
          <div>
            <h1 className={clsx('text-2xl font-bold', overall.tone)}>{overall.label}</h1>
            <p className="text-[11px] text-text-muted mt-0.5">
              {data.summary.operational} operational ·
              {data.summary.degraded > 0  && <span className="text-amber-light"> {data.summary.degraded} degraded ·</span>}
              {data.summary.outage > 0    && <span className="text-rose"> {data.summary.outage} outage ·</span>}
              {data.summary.unknown > 0   && <span className="text-text-muted"> {data.summary.unknown} unknown ·</span>}
              {' '}as of {formatDistanceToNow(new Date(data.generated_at), { addSuffix: true })}
            </p>
          </div>
        </div>
      </div>

      {Object.entries(grouped).map(([kind, items]) => (
        <div key={kind} className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-3 uppercase tracking-wider">{kind}</h2>
          <div className="space-y-1.5">
            {items.map(c => {
              const v = STATUS_VISUAL[c.status] || STATUS_VISUAL.unknown
              const I = v.icon
              return (
                <div key={c.name} className="flex items-center justify-between p-2 rounded border border-border">
                  <div className="flex items-center gap-2">
                    <I size={14} className={v.tone} />
                    <span className="text-text-primary font-medium text-xs">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px]">
                    <span className={v.tone}>{v.label}</span>
                    {c.checked_at && (
                      <span className="text-text-muted font-mono">
                        {formatDistanceToNow(new Date(c.checked_at), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      <div className="text-[10px] text-text-muted text-center">
        Auto-refreshes every 60s · public-facing /api/status-page · 60s server cache
      </div>
    </div>
  )
}
